import { AttachmentCategory, AttachmentType } from '@@types/api.types';
import { AttachmentsSection } from '@components/shared/attachments/attachments-section.component';
import { withForm } from '@components/ui/form/form';
import { FormField } from '@components/ui/form/shared.component';
import { Input } from '@components/ui/input';
import { useInnovatorGetTechnologyAttachments } from '@queries/attachments.query';
import type { BufferedAttachment } from '@queries/attachments.query';
import { FormLabel } from '../../../shared/form-label.component';
import { technologyFormOptions } from './technology-form-options';

export const TechnologyResearchStep = withForm({
  ...technologyFormOptions,
  props: {
    bufferedAttachments: [] as BufferedAttachment[],
    isEdit: false,
    onBufferEdit: undefined as unknown as ((clientId: string, data: BufferedAttachment) => void) | undefined,
    onBufferRemove: undefined as unknown as ((clientId: string) => void) | undefined,
    onBufferSave: undefined as unknown as ((data: BufferedAttachment) => void) | undefined,
    targetId: undefined as string | undefined,
  },
  render: function Render({ form, bufferedAttachments, isEdit, onBufferEdit, onBufferRemove, onBufferSave, targetId }) {
    const { data: technologyAttachments, isPending: isAttachmentsPending } = useInnovatorGetTechnologyAttachments({
      category: AttachmentCategory.RESEARCH_ARTEFACT,
      technologyId: targetId ?? '',
    });

    return (
      <div className="flex w-full flex-col items-start gap-4">
        <AttachmentsSection
          attachments={isEdit ? technologyAttachments : undefined}
          bufferedAttachments={isEdit ? undefined : bufferedAttachments}
          canEdit={true}
          category={AttachmentCategory.RESEARCH_ARTEFACT}
          defaultType={AttachmentType.URL}
          isAttachmentsPending={isEdit ? isAttachmentsPending : false}
          onBufferEdit={onBufferEdit}
          onBufferRemove={onBufferRemove}
          onBufferSave={onBufferSave}
          showHeader={true}
          targetId={targetId}
          targetType="technology"
          title="Research Artefacts"
          useFormLabel={true}
        />

        <form.AppForm>
          <form.AppField
            name="metadata.researchKeywords"
            mode="array"
          >
            {(field) => (
              <field.InputChipField
                allowDuplicates={false}
                description="Type keywords and press Enter to add them."
                label="Research & Development Keywords"
                placeholder="Type keyword and press Enter to add..."
                tooltip="Enter the most important words and short phrases that describe your technology, methods, materials, or problem. Use synonyms, acronyms, and specific classification codes (e.g., IPC/CPC) to improve matches across research papers and patents."
              />
            )}
          </form.AppField>
        </form.AppForm>

        <form.Subscribe selector={(state) => state.values.timeline?.startDate}>
          {(startDate) => (
            <>
              <form.AppField name="timeline.startDate">
                {(field) => (
                  <field.DatePickerField
                    clearable
                    label="Development Started"
                    placeholder="Select date"
                    tooltip="When did the focus shift from theoretical research to practical development of the technology?

This date will establish the initial timeline for your technology development, marking a TRL 1 score."
                  />
                )}
              </form.AppField>

              {startDate ? (
                <form.AppField name="timeline.validated">
                  {(field) => (
                    <FormField>
                      <FormLabel label="Has your research been validated by a third-party?" />
                      <div className="flex w-full flex-row gap-1">
                        <label className="flex items-center">
                          <Input
                            type="radio"
                            name="timeline.validated"
                            value="true"
                            checked={field.state.value === true}
                            onChange={() => field.handleChange(true)}
                          />
                          <span className="ml-2">Yes</span>
                        </label>
                        <label className="flex items-center ml-3">
                          <Input
                            type="radio"
                            name="timeline.validated"
                            value="false"
                            checked={field.state.value === false}
                            onChange={() => field.handleChange(false)}
                          />
                          <span className="ml-2">No</span>
                        </label>
                      </div>
                    </FormField>
                  )}
                </form.AppField>
              ) : null}
            </>
          )}
        </form.Subscribe>

        <form.Subscribe
          selector={(state) => ({
            startDate: state.values.timeline?.startDate,
            validated: state.values.timeline?.validated,
          })}
        >
          {({ startDate, validated }) =>
            startDate && validated ? (
              <form.AppField name="timeline.description">
                {(field) => (
                  <field.TextAreaField
                    label="Describe how your research was validated, please provide link(s) if relevant."
                    textareaClassName="w-full"
                  />
                )}
              </form.AppField>
            ) : null
          }
        </form.Subscribe>
      </div>
    );
  },
});
